import React, { useCallback } from 'react';
import {
  type AddMedicationRequest,
  type UpdateMedicationRequest,
  useAddMedicationMutation,
  useRemoveMedicationMutation,
  useUpdateMedicationMutation,
} from '@features/patients';
import { useSubjectLanguageVariants } from '@features/user';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import type { Medication } from '../types';

import { PatientMedicationsEditCard } from './PatientMedicationsEditCard';

interface PatientMedicationsEditModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  medications: Medication[];
  onMedicationsUpdated?: () => void;
  /** Source identifier for analytics */
  source: string;
}

export const PatientMedicationsEditModal: React.FC<PatientMedicationsEditModalProps> = ({
  open,
  onClose,
  patientId,
  medications,
  onMedicationsUpdated,
  source,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { singularCap } = useSubjectLanguageVariants();
  const { patientContextBar: contextBarTracking } = useEventTracking();

  const [addMedication, { isLoading: isAdding }] = useAddMedicationMutation();
  const [updateMedication] = useUpdateMedicationMutation();
  const [removeMedication] = useRemoveMedicationMutation();

  const handleAdd = useCallback(
    async (request: AddMedicationRequest): Promise<Medication> => {
      try {
        const response = await addMedication({
          patientId,
          addMedicationRequest: request,
        }).unwrap();
        onMedicationsUpdated?.();
        return response.medication as Medication;
      } catch (error) {
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'medications',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [addMedication, patientId, onMedicationsUpdated, contextBarTracking, source],
  );

  const handleUpdate = useCallback(
    async (medicationId: string, patch: UpdateMedicationRequest): Promise<void> => {
      try {
        await updateMedication({
          patientId,
          medicationId,
          updateMedicationRequest: patch,
        }).unwrap();
        onMedicationsUpdated?.();
      } catch (error) {
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'medications',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [updateMedication, patientId, onMedicationsUpdated, contextBarTracking, source],
  );

  const handleDelete = useCallback(
    async (medicationId: string): Promise<void> => {
      try {
        await removeMedication({ patientId, medicationId }).unwrap();
        onMedicationsUpdated?.();
      } catch (error) {
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'medications',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [removeMedication, patientId, onMedicationsUpdated, contextBarTracking, source],
  );

  const handleClose = useCallback(() => {
    contextBarTracking.editModalClosed({
      patient_id: patientId,
      data_type: 'medications',
      had_changes: false,
      source,
    });
    onClose();
  }, [onClose, patientId, source, contextBarTracking]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '80vh',
          maxHeight: fullScreen ? '100%' : '80vh',
          bgcolor: 'background.paper',
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {`Edit ${singularCap} Medications`}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2 }}>
          <PatientMedicationsEditCard
            medications={medications}
            onAddMedication={handleAdd}
            onUpdateMedication={handleUpdate}
            onDeleteMedication={handleDelete}
            isLoading={isAdding}
            patientId={patientId}
            source={source}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
